import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const TestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userInput, setUserInput] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [isTestActive, setIsTestActive] = useState(false);

  useEffect(() => {
    const fetchTest = async () => {
      try {
        const response = await axios.get(`/api/tests/${id}`);
        setTest(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch test details');
        setLoading(false);
      }
    };

    fetchTest();
  }, [id]);

  const startTest = () => {
    setStartTime(Date.now());
    setIsTestActive(true);
  };

  const handleInputChange = (e) => {
    if (!isTestActive) return;
    setUserInput(e.target.value);
  };

  const submitTest = async () => {
    if (!isTestActive) return;

    const endTime = Date.now();
    const duration = (endTime - startTime) / 1000; // in seconds
    const words = userInput.trim().split(/\s+/).length;
    const wpm = Math.round((words / duration) * 60);

    try {
      await axios.post(`/api/tests/${id}/results`, {
        wpm,
        accuracy: calculateAccuracy(),
        duration,
        text: userInput
      });
      navigate('/results');
    } catch (err) {
      setError('Failed to submit test results');
    }
  };

  const calculateAccuracy = () => {
    if (!test || !userInput) return 0;
    const originalWords = test.text.trim().split(/\s+/);
    const userWords = userInput.trim().split(/\s+/);
    let correctWords = 0;

    for (let i = 0; i < Math.min(originalWords.length, userWords.length); i++) {
      if (originalWords[i] === userWords[i]) {
        correctWords++;
      }
    }

    return Math.round((correctWords / originalWords.length) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">{test?.title}</h1>

          {!isTestActive ? (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Test Instructions</h3>
                <div className="mt-2 max-w-xl text-sm text-gray-500">
                  <p>{test?.description}</p>
                </div>
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={startTest}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Start Test
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-white shadow sm:rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Test Text</h3>
                <p className="text-gray-600 whitespace-pre-wrap">{test?.text}</p>
              </div>

              <div className="bg-white shadow sm:rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Your Input</h3>
                <textarea
                  value={userInput}
                  onChange={handleInputChange}
                  className="w-full h-48 p-4 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Start typing here..."
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={submitTest}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Submit Test
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestDetail; 